// Requisitos del estudiante en el módulo de asesoras: qué documentos faltan,
// cuáles están pendientes, aprobados o rechazados, y la carga de documentos a
// nombre del estudiante (pendientes de revisión por administración).
import React from 'react'
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Link as ChakraLink,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react'
import { AddIcon, AttachmentIcon } from '@chakra-ui/icons'
import { useParams, useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'

import xhr from 'xhr'
import { EmptyState, PageHeader, SectionCard, StatusBadge } from '../../../components/ui'
import UploadDocumentModal, { stripHtml } from './UploadDocumentModal'

const STATUS = {
  approved: { label: 'Aprobado', color: 'green' },
  pending: { label: 'Pendiente', color: 'yellow' },
  denied: { label: 'Rechazado', color: 'red' },
  missing: { label: 'Falta', color: 'gray' },
}

const METHOD = { scanned: 'Escaneado', fisical: 'Físico' }

// Documentos que cubre un registro: el viejo campo "requirement" o la lista "requirements_many".
const coveredBy = (sitting) => (sitting.requirement ? [sitting.requirement] : sitting.requirements_many || [])

// Estado de cada requisito del programa según lo entregado: aprobado gana, luego pendiente, luego rechazado.
const requirementStatus = (requirement, sittings) => {
  const related = sittings.filter((s) => coveredBy(s).some((r) => r.id === requirement.id))
  if (related.some((s) => s.status === 'approved')) return { key: 'approved' }
  if (related.some((s) => s.status === 'pending')) return { key: 'pending' }
  const denied = related.find((s) => s.status === 'denied')
  if (denied) return { key: 'denied', comment: denied.comment }
  return { key: 'missing' }
}

export default function VerRequisitosAdviser() {
  const { id: studentId } = useParams()
  const [searchParams] = useSearchParams()
  const programTypeId = searchParams.get('program')
  const toast = useToast()

  const [requirements, setRequirements] = React.useState([])
  const [sittings, setSittings] = React.useState([])
  const [programName, setProgramName] = React.useState('')
  const [studentName, setStudentName] = React.useState('')
  const [loading, setLoading] = React.useState(true)
  const [modal, setModal] = React.useState(null) // 'many' | 'single' | null
  const [toDelete, setToDelete] = React.useState(null)
  const [deleting, setDeleting] = React.useState(false)
  const cancelRef = React.useRef()

  const loadSittings = React.useCallback(async () => {
    const res = await xhr(
      `/requirement-sittings?filters[student][id][$eq]=${studentId}&filters[program_type][id][$eq]=${programTypeId}&populate=*&sort[0]=createdAt:desc&pagination[pageSize]=100`
    )
    setSittings(res.data.data || [])
  }, [studentId, programTypeId])

  React.useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        const [resRequirements, resProgram, resStudent] = await Promise.all([
          xhr(`/requirements?filters[program_type][id][$eq]=${programTypeId}&pagination[pageSize]=100`),
          // Nombre del programa y del estudiante: solo informativos, si fallan la pantalla sigue.
          xhr(`/program-types/${programTypeId}`).catch(() => ({ data: { data: null } })),
          xhr(`/students/${studentId}?populate=user`).catch(() => ({ data: { data: null } })),
          loadSittings(),
        ])
        setRequirements(resRequirements.data.data || [])
        setProgramName(resProgram.data.data?.name || '')
        const user = resStudent.data.data?.user
        setStudentName(user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '')
      } catch (error) {
        console.error(error)
        toast({ title: 'No se pudieron cargar los requisitos.', status: 'error', duration: 3000 })
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [studentId, programTypeId, loadSittings, toast])

  const handleDelete = async () => {
    try {
      setDeleting(true)
      await xhr.delete(`/requirement-sittings/${toDelete.id}/adviser`)
      toast({ title: 'Documento eliminado.', status: 'success', duration: 2500 })
      setToDelete(null)
      await loadSittings()
    } catch (error) {
      console.error(error)
      toast({
        title: error?.response?.data?.error?.message || 'No se pudo eliminar el documento.',
        status: 'error',
        duration: 4000,
      })
    } finally {
      setDeleting(false)
    }
  }

  const statusList = requirements.map((r) => ({ ...r, state: requirementStatus(r, sittings) }))
  const missingCount = statusList.filter((r) => r.state.key === 'missing' || r.state.key === 'denied').length

  return (
    <Box>
      <PageHeader
        breadcrumb={[
          { label: 'Ver estudiante', to: `/plataforma/asesoras/ver-estudiante/${studentId}` },
          { label: 'Requisitos' },
        ]}
        title="Requisitos"
        subtitle={[studentName, programName].filter(Boolean).join(' · ')}
        actions={
          <Flex gap={2} wrap="wrap">
            <Button size="sm" variant="outline" colorScheme="purple" leftIcon={<AddIcon boxSize={2.5} />} onClick={() => setModal('single')}>
              Añadir requisito
            </Button>
            <Button size="sm" colorScheme="purple" leftIcon={<AttachmentIcon />} onClick={() => setModal('many')}>
              Subir documento
            </Button>
          </Flex>
        }
      />

      {loading ? (
        <Flex justify="center" py={16}>
          <Spinner color="purple.500" />
        </Flex>
      ) : (
        <Flex direction="column" gap={5}>
          <SectionCard
            title="Documentos del programa"
            description={
              requirements.length === 0
                ? undefined
                : missingCount === 0
                ? 'Todos los documentos están entregados.'
                : `${missingCount} documento${missingCount === 1 ? '' : 's'} por entregar o corregir.`
            }
          >
            {requirements.length === 0 ? (
              <EmptyState title="Este programa no tiene requisitos configurados" />
            ) : (
              <Flex direction="column">
                {statusList.map((r) => (
                  <Flex
                    key={r.id}
                    justify="space-between"
                    align={{ base: 'flex-start', md: 'center' }}
                    gap={3}
                    py={2.5}
                    borderBottom="1px solid"
                    borderColor="gray.100"
                    _last={{ borderBottom: 0 }}
                    direction={{ base: 'column', md: 'row' }}
                  >
                    <Box minW={0}>
                      <Text fontSize="sm" color="gray.800">
                        {stripHtml(r.name)}
                      </Text>
                      {r.state.key === 'denied' && r.state.comment && (
                        <Text fontSize="xs" color="red.600">
                          Motivo del rechazo: {r.state.comment}
                        </Text>
                      )}
                    </Box>
                    <StatusBadge label={STATUS[r.state.key].label} color={STATUS[r.state.key].color} />
                  </Flex>
                ))}
              </Flex>
            )}
          </SectionCard>

          <SectionCard title="Documentos entregados">
            {sittings.length === 0 ? (
              <EmptyState
                title="Aún no hay documentos"
                description="Cuando el estudiante o tú suban un documento, aparecerá aquí."
              />
            ) : (
              <TableContainer>
                <Table size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Documento</Th>
                      <Th>Método</Th>
                      <Th>Estado</Th>
                      <Th>Archivo</Th>
                      <Th>Fecha</Th>
                      <Th textAlign="right">Acciones</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {sittings.map((s) => {
                      const status = STATUS[s.status] || STATUS.pending
                      return (
                        <Tr key={s.id}>
                          <Td whiteSpace="normal" minW="220px">
                            {coveredBy(s).map((r) => (
                              <Text key={r.id} fontSize="sm">
                                {stripHtml(r.name)}
                              </Text>
                            ))}
                          </Td>
                          <Td>{METHOD[s.method] || '—'}</Td>
                          <Td whiteSpace="normal" minW="140px">
                            <StatusBadge label={status.label} color={status.color} />
                            {s.status === 'denied' && s.comment && (
                              <Text fontSize="xs" color="red.600" mt={1}>
                                {s.comment}
                              </Text>
                            )}
                          </Td>
                          <Td>
                            {s.file?.url ? (
                              <ChakraLink href={s.file.url} isExternal color="purple.600" fontSize="sm">
                                Ver archivo
                              </ChakraLink>
                            ) : (
                              <Text fontSize="sm" color="gray.400">
                                Sin archivo
                              </Text>
                            )}
                          </Td>
                          <Td fontSize="sm">{dayjs(s.createdAt).format('DD/MM/YYYY')}</Td>
                          <Td textAlign="right">
                            {s.status !== 'approved' && (
                              <Button size="xs" variant="ghost" colorScheme="red" onClick={() => setToDelete(s)}>
                                Eliminar
                              </Button>
                            )}
                          </Td>
                        </Tr>
                      )
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </SectionCard>
        </Flex>
      )}

      <UploadDocumentModal
        mode={modal}
        isOpen={Boolean(modal)}
        onClose={() => setModal(null)}
        requirements={requirements}
        studentId={studentId}
        programTypeId={programTypeId}
        onSaved={loadSittings}
      />

      <AlertDialog isOpen={Boolean(toDelete)} leastDestructiveRef={cancelRef} onClose={() => setToDelete(null)} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="md">Eliminar documento</AlertDialogHeader>
            <AlertDialogBody fontSize="sm">
              Se eliminará el documento y su archivo:{' '}
              <b>{toDelete && coveredBy(toDelete).map((r) => stripHtml(r.name)).join(', ')}</b>. Queda registrado en el
              historial del estudiante.
            </AlertDialogBody>
            <AlertDialogFooter gap={3}>
              <Button ref={cancelRef} variant="ghost" onClick={() => setToDelete(null)}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={handleDelete} isLoading={deleting}>
                Eliminar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  )
}
